import type {
  RealEstateComputedScenario,
  RealEstateLineItem,
  RealEstateMetricSnapshot,
  RealEstateScenario,
} from "@features/real-estate/types";

type DerivedContext = {
  purchasePrice: number;
  loanAmount: number;
  rehabBudget: number;
  salePrice: number;
  grossScheduledRent: number;
  effectiveGrossIncome: number;
};

export function computeScenario(scenario: RealEstateScenario): RealEstateComputedScenario {
  const warnings: string[] = [];
  const purchasePrice = positiveOrZero(scenario.financing.purchasePrice);
  const grossScheduledRent = positiveOrZero(scenario.income.monthlyRent) * 12;
  const otherIncomeAnnual = positiveOrZero(scenario.income.otherMonthlyIncome) * 12;
  const vacancyRate = percentToRatio(scenario.income.vacancyRate);
  const effectiveGrossIncome = Math.max((grossScheduledRent * (1 - vacancyRate)) + otherIncomeAnnual, 0);
  const downPaymentAmount = purchasePrice * percentToRatio(scenario.financing.downPaymentPercent);
  const loanAmount = Math.max(purchasePrice - downPaymentAmount, 0);
  const monthlyDebtService = calculateMonthlyDebtService(
    loanAmount,
    positiveOrZero(scenario.financing.interestRate),
    positiveOrZero(scenario.financing.amortizationYears),
  );
  const annualDebtService = monthlyDebtService * 12;
  const capexLineItems = sumLineItems(
    scenario.lineItems,
    "capex",
    buildDerivedContext(purchasePrice, loanAmount, positiveOrZero(scenario.rehabCapex.rehabBudget), purchasePrice, grossScheduledRent, effectiveGrossIncome),
  );
  const rehabContingency = positiveOrZero(scenario.rehabCapex.rehabBudget) * percentToRatio(scenario.rehabCapex.rehabContingencyPercent);
  const totalCapex = positiveOrZero(scenario.rehabCapex.rehabBudget) + rehabContingency + capexLineItems;
  const acquisitionCosts = sumLineItems(
    scenario.lineItems,
    "acquisition",
    buildDerivedContext(purchasePrice, loanAmount, positiveOrZero(scenario.rehabCapex.rehabBudget), purchasePrice, grossScheduledRent, effectiveGrossIncome),
  );
  const annualOperatingExpenses = calculateAnnualOperatingExpenses(scenario, grossScheduledRent, effectiveGrossIncome, purchasePrice, loanAmount);
  const annualReserves = calculateAnnualReserves(scenario, grossScheduledRent, effectiveGrossIncome, purchasePrice, loanAmount);
  const noi = effectiveGrossIncome - annualOperatingExpenses;
  const annualCashFlow = noi - annualDebtService - annualReserves;
  const monthlyCashFlow = annualCashFlow / 12;
  const cashToClose = downPaymentAmount + acquisitionCosts + totalCapex;
  const salePriceAtExit = calculateSalePriceAtExit(purchasePrice, positiveOrZero(scenario.exitAssumptions.appreciationRate), positiveOrZero(scenario.exitAssumptions.holdPeriodMonths));
  const exitLineItems = sumLineItems(
    scenario.lineItems,
    "exit",
    buildDerivedContext(purchasePrice, loanAmount, positiveOrZero(scenario.rehabCapex.rehabBudget), salePriceAtExit, grossScheduledRent, effectiveGrossIncome),
  );
  const exitCosts = (salePriceAtExit * percentToRatio(scenario.exitAssumptions.saleCostsPercent)) + exitLineItems;
  const remainingBalance = calculateRemainingPrincipal(
    loanAmount,
    positiveOrZero(scenario.financing.interestRate),
    positiveOrZero(scenario.financing.amortizationYears),
    positiveOrZero(scenario.exitAssumptions.holdPeriodMonths),
  );
  const netSaleProceeds = salePriceAtExit - exitCosts - remainingBalance;

  if (purchasePrice <= 0) {
    warnings.push("Purchase price is missing. Results are not meaningful until the base deal value is set.");
  }
  if (grossScheduledRent <= 0) {
    warnings.push("Monthly rent is zero. Income-driven metrics will stay muted until you underwrite rent.");
  }
  if (cashToClose <= 0) {
    warnings.push("Cash to close is zero or negative. Check purchase price, down payment, and one-time costs.");
  }

  const metrics: RealEstateMetricSnapshot = {
    grossScheduledRent: nullableNumber(grossScheduledRent),
    effectiveGrossIncome: nullableNumber(effectiveGrossIncome),
    noi: nullableNumber(noi),
    monthlyDebtService: nullableNumber(monthlyDebtService),
    monthlyCashFlow: nullableNumber(monthlyCashFlow),
    capRate: ratio(noi, purchasePrice),
    cashOnCashReturn: ratio(annualCashFlow, cashToClose),
    dscr: ratio(noi, annualDebtService),
    breakevenOccupancy: ratio(annualOperatingExpenses + annualReserves + annualDebtService, grossScheduledRent + otherIncomeAnnual),
    leveredIRR: calculateLeveredIRR(scenario, cashToClose),
    equityMultiple: calculateEquityMultiple(scenario, cashToClose),
    cashToClose: nullableNumber(cashToClose),
    annualOperatingExpenses: nullableNumber(annualOperatingExpenses),
    annualReserves: nullableNumber(annualReserves),
    annualDebtService: nullableNumber(annualDebtService),
    acquisitionCosts: nullableNumber(acquisitionCosts),
    totalCapex: nullableNumber(totalCapex),
    salePriceAtExit: nullableNumber(salePriceAtExit),
    netSaleProceeds: nullableNumber(netSaleProceeds),
  };

  return { scenario, metrics, warnings };
}

export function computeScenarioCollection(scenarios: RealEstateScenario[]): RealEstateComputedScenario[] {
  return scenarios.map((scenario) => computeScenario(scenario));
}

function calculateAnnualOperatingExpenses(
  scenario: RealEstateScenario,
  grossScheduledRent: number,
  effectiveGrossIncome: number,
  purchasePrice: number,
  loanAmount: number,
): number {
  const fixedBase = positiveOrZero(scenario.operatingExpenses.annualPropertyTax)
    + positiveOrZero(scenario.operatingExpenses.annualInsurance);
  const management = effectiveGrossIncome * percentToRatio(scenario.operatingExpenses.propertyManagementPercent);
  const maintenance = grossScheduledRent * percentToRatio(scenario.operatingExpenses.maintenancePercent);
  const additional = sumLineItems(
    scenario.lineItems,
    "operating",
    buildDerivedContext(purchasePrice, loanAmount, positiveOrZero(scenario.rehabCapex.rehabBudget), purchasePrice, grossScheduledRent, effectiveGrossIncome),
  );
  return fixedBase + management + maintenance + additional;
}

function calculateAnnualReserves(
  scenario: RealEstateScenario,
  grossScheduledRent: number,
  effectiveGrossIncome: number,
  purchasePrice: number,
  loanAmount: number,
): number {
  const base = grossScheduledRent * percentToRatio(scenario.reserves.capitalReservesPercent);
  const extras = sumLineItems(
    scenario.lineItems,
    "reserve",
    buildDerivedContext(purchasePrice, loanAmount, positiveOrZero(scenario.rehabCapex.rehabBudget), purchasePrice, grossScheduledRent, effectiveGrossIncome),
  );
  return base + extras;
}

function sumLineItems(
  lineItems: RealEstateLineItem[],
  section: RealEstateLineItem["section"],
  context: DerivedContext,
): number {
  return lineItems.reduce((total, item) => {
    if (item.section !== section || !item.enabled) {
      return total;
    }
    return total + evaluateLineItem(item, context);
  }, 0);
}

function evaluateLineItem(item: RealEstateLineItem, context: DerivedContext): number {
  const amount = positiveOrZero(item.amount);
  switch (item.calcType) {
    case "fixed_annual":
    case "one_time":
      return amount;
    case "fixed_monthly":
      return amount * 12;
    case "percent_of_gross_rent":
      return context.grossScheduledRent * percentToRatio(amount);
    case "percent_of_egi":
      return context.effectiveGrossIncome * percentToRatio(amount);
    case "percent_of_purchase_price":
      return context.purchasePrice * percentToRatio(amount);
    case "percent_of_loan":
      return context.loanAmount * percentToRatio(amount);
    case "percent_of_rehab":
      return context.rehabBudget * percentToRatio(amount);
    case "percent_of_sale_price":
      return context.salePrice * percentToRatio(amount);
    default:
      return 0;
  }
}

function calculateMonthlyDebtService(principal: number, annualRate: number, amortizationYears: number): number {
  if (principal <= 0) {
    return 0;
  }
  const payments = amortizationYears * 12;
  if (payments <= 0) {
    return 0;
  }
  const monthlyRate = annualRate / 100 / 12;
  if (monthlyRate === 0) {
    return principal / payments;
  }
  const factor = Math.pow(1 + monthlyRate, payments);
  return principal * ((monthlyRate * factor) / (factor - 1));
}

function calculateRemainingPrincipal(principal: number, annualRate: number, amortizationYears: number, monthsPaid: number): number {
  if (principal <= 0) {
    return 0;
  }
  const payments = amortizationYears * 12;
  if (payments <= 0) {
    return 0;
  }
  const monthlyRate = annualRate / 100 / 12;
  if (monthlyRate === 0) {
    return Math.max(principal - ((principal / payments) * monthsPaid), 0);
  }
  const payment = calculateMonthlyDebtService(principal, annualRate, amortizationYears);
  const balance = principal * Math.pow(1 + monthlyRate, monthsPaid) - payment * ((Math.pow(1 + monthlyRate, monthsPaid) - 1) / monthlyRate);
  return Math.max(balance, 0);
}

function calculateSalePriceAtExit(purchasePrice: number, appreciationRate: number, holdPeriodMonths: number): number {
  if (purchasePrice <= 0) {
    return 0;
  }
  return purchasePrice * Math.pow(1 + (appreciationRate / 100), holdPeriodMonths / 12);
}

function calculateLeveredIRR(scenario: RealEstateScenario, cashToClose: number): number | null {
  const cashFlows = buildMonthlyCashFlows(scenario, cashToClose);
  if (cashFlows.length < 2) {
    return null;
  }
  const monthlyRate = solveIRR(cashFlows);
  if (monthlyRate === null) {
    return null;
  }
  return Math.pow(1 + monthlyRate, 12) - 1;
}

function calculateEquityMultiple(scenario: RealEstateScenario, cashToClose: number): number | null {
  if (cashToClose <= 0) {
    return null;
  }
  const cashFlows = buildMonthlyCashFlows(scenario, cashToClose);
  const positiveInflows = cashFlows.reduce((total, value) => total + (value > 0 ? value : 0), 0);
  return positiveInflows / cashToClose;
}

function buildMonthlyCashFlows(scenario: RealEstateScenario, cashToClose: number): number[] {
  if (cashToClose <= 0) {
    return [];
  }
  const holdMonths = Math.max(Math.round(positiveOrZero(scenario.exitAssumptions.holdPeriodMonths)), 1);
  const monthlyDebt = calculateMonthlyDebtService(
    positiveOrZero(scenario.financing.purchasePrice) * (1 - percentToRatio(scenario.financing.downPaymentPercent)),
    positiveOrZero(scenario.financing.interestRate),
    positiveOrZero(scenario.financing.amortizationYears),
  );
  const baseRent = positiveOrZero(scenario.income.monthlyRent);
  const otherIncome = positiveOrZero(scenario.income.otherMonthlyIncome);
  const vacancyRate = percentToRatio(scenario.income.vacancyRate);
  const rentGrowth = scenario.income.annualRentGrowthRate / 100;
  const expenseGrowth = scenario.operatingExpenses.annualExpenseGrowthRate / 100;
  const purchasePrice = positiveOrZero(scenario.financing.purchasePrice);
  const loanAmount = purchasePrice * (1 - percentToRatio(scenario.financing.downPaymentPercent));
  const flows = [-cashToClose];

  for (let month = 1; month <= holdMonths; month += 1) {
    const yearIndex = Math.floor((month - 1) / 12);
    const monthlyRent = baseRent * Math.pow(1 + rentGrowth, yearIndex);
    const grossScheduledRent = monthlyRent * 12;
    const effectiveGrossIncome = (grossScheduledRent * (1 - vacancyRate)) + (otherIncome * 12);
    const annualOperatingBase = positiveOrZero(scenario.operatingExpenses.annualPropertyTax)
      + positiveOrZero(scenario.operatingExpenses.annualInsurance);
    const scaledOperatingBase = annualOperatingBase * Math.pow(1 + expenseGrowth, yearIndex);
    const management = effectiveGrossIncome * percentToRatio(scenario.operatingExpenses.propertyManagementPercent);
    const maintenance = grossScheduledRent * percentToRatio(scenario.operatingExpenses.maintenancePercent);
    const operatingExtras = scenario.lineItems
      .filter((item) => item.section === "operating")
      .reduce((total, item) => {
        const base = evaluateLineItem(item, buildDerivedContext(purchasePrice, loanAmount, scenario.rehabCapex.rehabBudget, purchasePrice, grossScheduledRent, effectiveGrossIncome));
        return total + (item.calcType === "fixed_annual" || item.calcType === "fixed_monthly" ? base * Math.pow(1 + expenseGrowth, yearIndex) : base);
      }, 0);
    const reserveExtras = scenario.lineItems
      .filter((item) => item.section === "reserve")
      .reduce((total, item) => {
        const base = evaluateLineItem(item, buildDerivedContext(purchasePrice, loanAmount, scenario.rehabCapex.rehabBudget, purchasePrice, grossScheduledRent, effectiveGrossIncome));
        return total + (item.calcType === "fixed_annual" || item.calcType === "fixed_monthly" ? base * Math.pow(1 + expenseGrowth, yearIndex) : base);
      }, 0);
    const reserves = (grossScheduledRent * percentToRatio(scenario.reserves.capitalReservesPercent)) + reserveExtras;
    const noi = effectiveGrossIncome - scaledOperatingBase - management - maintenance - operatingExtras;
    let monthlyCashFlow = (noi - reserves) / 12 - monthlyDebt;

    if (month === holdMonths) {
      const salePrice = calculateSalePriceAtExit(purchasePrice, positiveOrZero(scenario.exitAssumptions.appreciationRate), holdMonths);
      const exitLineItems = sumLineItems(
        scenario.lineItems,
        "exit",
        buildDerivedContext(purchasePrice, loanAmount, scenario.rehabCapex.rehabBudget, salePrice, grossScheduledRent, effectiveGrossIncome),
      );
      const exitCosts = (salePrice * percentToRatio(scenario.exitAssumptions.saleCostsPercent)) + exitLineItems;
      const remainingPrincipal = calculateRemainingPrincipal(
        loanAmount,
        positiveOrZero(scenario.financing.interestRate),
        positiveOrZero(scenario.financing.amortizationYears),
        holdMonths,
      );
      monthlyCashFlow += salePrice - exitCosts - remainingPrincipal;
    }

    flows.push(monthlyCashFlow);
  }

  return flows;
}

function solveIRR(cashFlows: number[]): number | null {
  const hasNegative = cashFlows.some((value) => value < 0);
  const hasPositive = cashFlows.some((value) => value > 0);
  if (!hasNegative || !hasPositive) {
    return null;
  }

  let lower = -0.95;
  let upper = 1;
  let npvLower = npv(cashFlows, lower);
  let npvUpper = npv(cashFlows, upper);

  for (let attempts = 0; attempts < 12 && npvLower * npvUpper > 0; attempts += 1) {
    upper *= 2;
    npvUpper = npv(cashFlows, upper);
  }
  if (npvLower * npvUpper > 0) {
    return null;
  }

  for (let iteration = 0; iteration < 100; iteration += 1) {
    const mid = (lower + upper) / 2;
    const npvMid = npv(cashFlows, mid);
    if (Math.abs(npvMid) < 1e-7) {
      return mid;
    }
    if (npvLower * npvMid <= 0) {
      upper = mid;
      npvUpper = npvMid;
    } else {
      lower = mid;
      npvLower = npvMid;
    }
  }
  return (lower + upper) / 2;
}

function npv(cashFlows: number[], rate: number): number {
  return cashFlows.reduce((total, cashFlow, index) => total + (cashFlow / Math.pow(1 + rate, index)), 0);
}

function buildDerivedContext(
  purchasePrice: number,
  loanAmount: number,
  rehabBudget: number,
  salePrice: number,
  grossScheduledRent: number,
  effectiveGrossIncome: number,
): DerivedContext {
  return {
    purchasePrice,
    loanAmount,
    rehabBudget,
    salePrice,
    grossScheduledRent,
    effectiveGrossIncome,
  };
}

function percentToRatio(value: number): number {
  return positiveOrZero(value) / 100;
}

function positiveOrZero(value: number): number {
  if (!Number.isFinite(value) || value < 0) {
    return 0;
  }
  return value;
}

function ratio(numerator: number, denominator: number): number | null {
  if (!Number.isFinite(numerator) || !Number.isFinite(denominator) || denominator <= 0) {
    return null;
  }
  return numerator / denominator;
}

function nullableNumber(value: number): number | null {
  if (!Number.isFinite(value)) {
    return null;
  }
  return value;
}
